import { useState, useContext, useEffect } from "react";
import { FaChartBar, FaHome, FaRegAddressCard, FaFolder } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { FaCog } from "react-icons/fa";
import { FaVolumeUp, FaVolumeMute } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.css";
import { Context2 } from "./Audio";
import PilotButton from "./PilotButton";
import { isDemoUser } from "@/utils/userFlags";
import { useBootstrap } from "@/features/bootstrap";
import { useAppAccess } from "@/features/access-control";

const VolumeToggle = ({ rowClass, iconClass, changeAudio, playing }) => (
  <div className="menuaction">
    <div className={rowClass} onClick={() => changeAudio(!playing)}>
      <div className={iconClass}>
        {playing ? <FaVolumeUp /> : <FaVolumeMute />}
      </div>
      <h1 className="icontext">Volume</h1>
    </div>
    <p className="linklabel"> Turn sound on/off </p>
  </div>
);

const Sidebar = () => {
  const { data: user } = useBootstrap();
  const { isPaused } = useAppAccess();

  const [playing, setPlaying] = useContext(Context2);
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [onMobile, setOnMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 640;
  });
  const isDemo = isDemoUser(user);

  const toggleSidebar = () => {
    setIsActive(!isActive);
  };

  async function changeAudio(newPlaying) {
    document.cookie = `audio=${newPlaying}; path=/; SameSite=Strict; Secure`;
    setPlaying(newPlaying);
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleResize = () => setOnMobile(window.innerWidth <= 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const sidebarColumn = (
    <div
      className={`sidebarcolumn ${isActive ? "active" : ""}`}
      onClick={toggleSidebar}
      data-onboarding-id="sidebar-nav"
    >
      <div className="sidebarrow">
        <Link to="/" style={{ textDecoration: "none" }}>
          <img
            className="sidebarlogo"
            src="images/123WellnessLogo green-02.png"
            alt="1-2-3 Wellness"
            loading="lazy"
          />
        </Link>
        {user?.role === "student" && (
          <div className="alignMenuOptions">
            <Link to="/" className={location.pathname === "/" ? "active" : ""}>
              <div className="icontextrowstudent">
                <div className="styleiconstudenthome">
                  <FaHome></FaHome>
                </div>
                <h1 className="icontexthome">Home</h1>
              </div>
              <p className="linklabel">Start practicing wellness</p>
            </Link>
            <Link
              to="/studentdashboard"
              className={
                location.pathname === "/studentdashboard" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaUser></FaUser>
                </div>
                <h1 className="icontext">My Story</h1>
              </div>
              <p className="linklabel">Learn about YOUR wellness</p>
            </Link>
            {!isPaused && (
              <VolumeToggle
                rowClass="icontextrowadmin"
                iconClass="styleiconadmin"
                changeAudio={changeAudio}
                playing={playing}
              />
            )}
            {!isDemo && (
              <Link
                to="/resourcestab"
                className={
                  location.pathname === "/resourcestab" ? "active" : ""
                }
              >
                <div className="icontextrowadmin">
                  <div className="styleiconadmin">
                    <FaFolder></FaFolder>
                  </div>
                  <h1 className="icontext">Resources</h1>
                </div>
                <p className="linklabel">Access powerful tools and videos</p>
              </Link>
            )}
            <Link
              to="/optionsscreen"
              className={location.pathname === "/optionsscreen" ? "active" : ""}
              data-onboarding-id="sidebar-settings"
            >
              <div className="icontextrowstudent">
                <div className="styleiconstudent">
                  <FaCog></FaCog>
                </div>
                <h1 className="icontext">Settings</h1>
              </div>
              <p className="linklabel">Manage your profile</p>
            </Link>
          </div>
        )}
        {user?.role === "teacher" && (
          <div className="alignMenuOptions">
            <Link to="/" className={location.pathname === "/" ? "active" : ""}>
              <div className="icontextrowteacher">
                <div className="styleiconteacherhome">
                  <FaHome></FaHome>
                </div>
                <h1 className="icontexthome">Home</h1>
              </div>
              <p className="linklabel">Start practicing wellness</p>
            </Link>
            <Link
              to="/studentdashboard"
              className={
                location.pathname === "/studentdashboard" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaUser></FaUser>
                </div>
                <h1 className="icontext">My Story</h1>
              </div>
              <p className="linklabel">Learn about YOUR wellness</p>
            </Link>
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaChartBar></FaChartBar>
                </div>
                <h1 className="icontext">Educator Dashboard</h1>
              </div>
              <p className="linklabel">Learn about your STUDENT'S wellness</p>
            </Link>
            <Link
              to="/studentsupports"
              className={
                location.pathname === "/studentsupports" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaRegAddressCard />
                </div>
                <h1 className="icontext">Student Supports</h1>
              </div>
              <p className="linklabel">
                Learn WHO may need MORE SUPPORT (tier 2 & 3 MTSS)
              </p>
            </Link>
            <VolumeToggle
              rowClass="icontextrowadmin"
              iconClass="styleiconadmin"
              changeAudio={changeAudio}
              playing={playing}
            />
            {!isDemo && (
              <Link
                to="/resourcestab"
                className={
                  location.pathname === "/resourcestab" ? "active" : ""
                }
              >
                <div className="icontextrowadmin">
                  <div className="styleiconadmin">
                    <FaFolder></FaFolder>
                  </div>
                  <h1 className="icontext">Resources</h1>
                </div>
                <p className="linklabel">Access powerful tools and videos</p>
              </Link>
            )}
            <Link
              to="/optionsscreen"
              className={location.pathname === "/optionsscreen" ? "active" : ""}
              data-onboarding-id="sidebar-settings"
            >
              <div className="icontextrowteacher">
                <div className="styleiconteacher">
                  <FaCog></FaCog>
                </div>
                <h1 className="icontext">Settings</h1>
              </div>
              <p className="linklabel">Manage your profile</p>
            </Link>
          </div>
        )}
        {user?.role === "admin" && (
          <div className="alignMenuOptions">
            <Link to="/" className={location.pathname === "/" ? "active" : ""}>
              <div className="icontextrowadmin">
                <div className="styleiconadminhome">
                  <FaHome></FaHome>
                </div>
                <h1 className="icontexthome">Home</h1>
              </div>
              <p className="linklabel">Start practicing wellness</p>
            </Link>
            <Link
              to="/studentdashboard"
              className={
                location.pathname === "/studentdashboard" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaUser></FaUser>
                </div>
                <h1 className="icontext">My Story</h1>
              </div>
              <p className="linklabel">Learn about YOUR wellness</p>
            </Link>
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaChartBar></FaChartBar>
                </div>
                <h1 className="icontext">Educator Dashboard</h1>
              </div>
              <p className="linklabel">Learn about your STUDENT'S wellness</p>
            </Link>
            <Link
              to="/studentsupports"
              className={
                location.pathname === "/studentsupports" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaRegAddressCard />
                </div>
                <h1 className="icontext">Student Supports</h1>
              </div>
              <p className="linklabel">
                Learn WHO may need MORE SUPPORT (tier 2 & 3 MTSS)
              </p>
            </Link>
            <VolumeToggle
              rowClass="icontextrowadmin"
              iconClass="styleiconadmin"
              changeAudio={changeAudio}
              playing={playing}
            />
            {!isDemo && (
              <Link
                to="/resourcestab"
                className={
                  location.pathname === "/resourcestab" ? "active" : ""
                }
              >
                <div className="icontextrowadmin">
                  <div className="styleiconadmin">
                    <FaFolder></FaFolder>
                  </div>
                  <h1 className="icontext">Resources</h1>
                </div>
                <p className="linklabel">Access powerful tools and videos</p>
              </Link>
            )}
            <Link
              to="/optionsscreen"
              className={location.pathname === "/optionsscreen" ? "active" : ""}
              data-onboarding-id="sidebar-settings"
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaCog></FaCog>
                </div>
                <h1 className="icontext">Settings</h1>
              </div>
              <p className="linklabel">Manage your profile</p>
            </Link>
          </div>
        )}
        {user?.role === "superadmin" && (
          <div className="alignMenuOptions">
            <Link to="/" className={location.pathname === "/" ? "active" : ""}>
              <div className="icontextrowadmin">
                <div className="styleiconadminhome">
                  <FaHome></FaHome>
                </div>
                <h1 className="icontexthome">Home</h1>
              </div>
              <p className="linklabel">Start practicing wellness</p>
            </Link>
            <Link
              to="/studentdashboard"
              className={
                location.pathname === "/studentdashboard" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaUser></FaUser>
                </div>
                <h1 className="icontext">My Story</h1>
              </div>
              <p className="linklabel">Learn about YOUR wellness</p>
            </Link>
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaChartBar></FaChartBar>
                </div>
                <h1 className="icontext">Educator Dashboard</h1>
              </div>
              <p className="linklabel">Learn about your STUDENT'S wellness</p>
            </Link>
            <Link
              to="/studentsupports"
              className={
                location.pathname === "/studentsupports" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaRegAddressCard />
                </div>
                <h1 className="icontext">Student Supports</h1>
              </div>
              <p className="linklabel">
                Learn WHO may need MORE SUPPORT (tier 2 & 3 MTSS)
              </p>
            </Link>
            <VolumeToggle
              rowClass="icontextrowadmin"
              iconClass="styleiconadmin"
              changeAudio={changeAudio}
              playing={playing}
            />
            {!isDemo && (
              <Link
                to="/resourcestab"
                className={
                  location.pathname === "/resourcestab" ? "active" : ""
                }
              >
                <div className="icontextrowadmin">
                  <div className="styleiconadmin">
                    <FaFolder></FaFolder>
                  </div>
                  <h1 className="icontext">Resources</h1>
                </div>
                <p className="linklabel">Access powerful tools and videos</p>
              </Link>
            )}
            <Link
              to="/optionsscreen"
              className={location.pathname === "/optionsscreen" ? "active" : ""}
              data-onboarding-id="sidebar-settings"
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaCog></FaCog>
                </div>
                <h1 className="icontext">Settings</h1>
              </div>
              <p className="linklabel">Manage your profile</p>
            </Link>
          </div>
        )}
        {user?.role === "123wellness" && (
          <div className="alignMenuOptions">
            <Link to="/" className={location.pathname === "/" ? "active" : ""}>
              <div className="icontextrowadmin">
                <div className="styleiconadminhome">
                  <FaHome></FaHome>
                </div>
                <h1 className="icontexthome">Home</h1>
              </div>
              <p className="linklabel">Start practicing wellness</p>
            </Link>
            <Link
              to="/studentdashboard"
              className={
                location.pathname === "/studentdashboard" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaUser></FaUser>
                </div>
                <h1 className="icontext">My Story</h1>
              </div>
              <p className="linklabel">Learn about YOUR wellness</p>
            </Link>
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "active" : ""}
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaChartBar></FaChartBar>
                </div>
                <h1 className="icontext">Educator Dashboard</h1>
              </div>
              <p className="linklabel">Learn about a STUDENT'S wellness</p>
            </Link>
            <Link
              to="/studentsupports"
              className={
                location.pathname === "/studentsupports" ? "active" : ""
              }
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaRegAddressCard />
                </div>
                <h1 className="icontext">Student Supports</h1>
              </div>
              <p className="linklabel">
                Learn WHO may need MORE SUPPORT (tier 2 & 3 MTSS)
              </p>
            </Link>

            <VolumeToggle
              rowClass="icontextrowadmin"
              iconClass="styleiconadmin"
              changeAudio={changeAudio}
              playing={playing}
            />
            {!isDemo && (
              <Link
                to="/resourcestab"
                className={
                  location.pathname === "/resourcestab" ? "active" : ""
                }
              >
                <div className="icontextrowadmin">
                  <div className="styleiconadmin">
                    <FaFolder></FaFolder>
                  </div>
                  <h1 className="icontext">Resources</h1>
                </div>
                <p className="linklabel">Access powerful tools and videos</p>
              </Link>
            )}
            <Link
              to="/optionsscreen"
              className={location.pathname === "/optionsscreen" ? "active" : ""}
              data-onboarding-id="sidebar-settings"
            >
              <div className="icontextrowadmin">
                <div className="styleiconadmin">
                  <FaCog></FaCog>
                </div>
                <h1 className="icontext">Settings</h1>
              </div>
              <p className="linklabel">Manage your profile</p>
            </Link>
          </div>
        )}
      </div>
      <PilotButton variant="sidebar" />

      <div className="schoolanddistrict">
        <div className="account-type">
          {user?.role === "123wellness" && <div>1-2-3 Wellness™ Leader</div>}
          {user?.role === "superadmin" && <div>Superadmin</div>}
          {user?.role === "admin" && <div>Administrator</div>}
          {user?.role === "teacher" && <div>Teacher</div>}
          {user?.role === "student" && <div>Student</div>}
        </div>
        {user?.role !== "123wellness" && (
          <ul>
            {user?.districts?.map((district, idx) => (
              <li key={idx}>{district.name}</li>
            ))}
          </ul>
        )}
        {user?.role === "123wellness" && <div>1-2-3 Wellness™ View</div>}
      </div>

      <div className="growthwell">
        <p>© Growthwell LLC {new Date().getFullYear()}</p>
        <p>U.S. Patent No. 12,053,300</p>
      </div>
    </div>
  );

  return onMobile ? (
    <div>
      <div className={`ham ${isActive ? "active" : ""}`}>
        <div>
          <img
            className="subtab-logo"
            src="images/123WellnessLogo green-02.png"
            alt="1-2-3 Wellness"
            loading="lazy"
          />
        </div>
        <div
          className={`ham-menu ${isActive ? "active" : ""}`}
          onClick={toggleSidebar}
          data-onboarding-id="sidebar-nav"
          data-onboarding-mobile-id="sidebar-hamburger"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      <div className="group/sidebar sidebar">{sidebarColumn}</div>
    </div>
  ) : (
    <div className="group/sidebar sidebar">{sidebarColumn}</div>
  );
};

export default Sidebar;
